import React from "react";
import { Users as UsersIcon, Trash2, Check, UserPlus, ChevronDown, X } from "lucide-react";
import { DatePicker } from "@/components/ui/date-time-picker";
import CountrySelect from "@/components/ui/CountrySelect";
import { paxHasContent } from "@/lib/newTripConstants";

// Suggestions dropdown lives in its own memo'd component so editing one
// passenger row doesn't re-run the autocomplete filter for every OTHER row
// in the leg (the IIFE used to live inline in the parent map).
const PassengerSuggestions = React.memo(function PassengerSuggestions({
  paxFirst,
  paxMiddle,
  paxLast,
  legPassengers,
  paxIndex,
  currentOwnerPassengers,
  onSelect,
}) {
  const typedFirst = (paxFirst || "").trim().toLowerCase();
  const typedMiddle = (paxMiddle || "").trim().toLowerCase();
  const typedLast = (paxLast || "").trim().toLowerCase();
  // Match against the CONJUNCTION of every field the user has actually typed:
  // first name only narrows by first name; once a last name is added the list
  // must satisfy BOTH; a middle name narrows it further. Each provided field is
  // an AND constraint, so a contradicting field always removes a candidate.
  const constraints = [
    [typedFirst, "first_name"],
    [typedMiddle, "middle_name"],
    [typedLast, "last_name"],
  ].filter(([v]) => v);
  // Let the user dismiss the dropdown. We remember WHICH typed combo was
  // dismissed so the suggestions reappear once they change the name again,
  // instead of staying hidden forever.
  const typedKey = `${typedFirst}|${typedMiddle}|${typedLast}`;
  const [dismissedKey, setDismissedKey] = React.useState(null);
  // Dismiss on Escape or a click outside the dropdown, in addition to the X
  // button — the X alone was too easy to miss (UX complaint). Stays closed for
  // this exact typed name; reappears once the name changes.
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setDismissedKey(typedKey);
    };
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setDismissedKey(typedKey);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [typedKey]);
  if (constraints.length === 0) return null;
  if (dismissedKey === typedKey) return null;
  const takenIds = new Set(
    (legPassengers || [])
      .map((p, i) => {
        if (i === paxIndex) return null;
        if (!p.passenger_id) return null;
        const hasName = (p.first_name || "").trim() || (p.last_name || "").trim();
        return hasName ? p.passenger_id : null;
      })
      .filter(Boolean)
  );
  const available = currentOwnerPassengers.filter((p) => !takenIds.has(p.id));
  // Hide once every typed field already equals the candidate exactly — at that
  // point there's nothing left to pick.
  const exact = available.some((p) =>
    constraints.every(([v, field]) => (p[field] || "").toLowerCase() === v)
  );
  if (exact) return null;
  const matches = available
    .filter((p) => constraints.every(([v, field]) => (p[field] || "").toLowerCase().includes(v)))
    .slice(0, 5);
  if (matches.length === 0) return null;
  return (
    <div
      ref={ref}
      className="absolute top-full left-0 right-0 mt-1 bg-[#1e293b] border border-slate-700 rounded-lg shadow-xl z-10 max-h-32 overflow-y-auto"
    >
      <button
        type="button"
        onClick={() => setDismissedKey(typedKey)}
        className="absolute top-1 right-1 z-10 p-0.5 text-slate-500 hover:text-white"
        aria-label="Cerrar sugerencias"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      {matches.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onSelect(p)}
          className="w-full px-3 py-2 text-left text-sm text-white hover:bg-slate-700 flex items-center gap-2"
        >
          <Check className="w-3 h-3 text-green-400" />
          {`${p.first_name || ""} ${p.last_name || ""}`.trim()}
          {p.passport_number && (
            <span className="text-[10px] text-slate-400 ml-auto">{p.passport_number}</span>
          )}
        </button>
      ))}
    </div>
  );
});

// Explicit "pick a saved passenger" control. The autocomplete only fires once
// you start TYPING a name — so a user who doesn't remember the exact name (or
// doesn't know saved passengers exist) ends up re-capturing everything by hand.
// This makes the saved roster discoverable: one click → dropdown of the
// company's passengers → fills name + passport from the master record. Born
// from the first-client audit (F-pax-reuse): the whole point of storing
// passport data is so it autofills when scheduling a flight.
export const SavedPassengerPicker = React.memo(function SavedPassengerPicker({
  legPassengers,
  paxIndex,
  currentOwnerPassengers,
  onSelect,
  t,
}) {
  const [open, setOpen] = React.useState(false);
  // Drop passengers already chosen in another row of THIS leg so the same
  // person can't be added twice.
  const takenIds = new Set(
    (legPassengers || [])
      .map((p, i) => (i === paxIndex ? null : p.passenger_id || null))
      .filter(Boolean)
  );
  const available = currentOwnerPassengers.filter((p) => !takenIds.has(p.id));
  if (available.length === 0) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
      >
        <UserPlus className="w-3.5 h-3.5" />
        {t("newTrip.selectSavedPassenger")}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 max-w-[80vw] bg-[#1e293b] border border-slate-700 rounded-lg shadow-xl z-20 max-h-48 overflow-y-auto">
          {available.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                onSelect(p);
                setOpen(false);
              }}
              className="w-full px-3 py-2 text-left text-sm text-white hover:bg-slate-700 flex items-center gap-2"
            >
              <Check className="w-3 h-3 text-green-400" />
              {`${p.first_name || ""} ${p.last_name || ""}`.trim()}
              {p.passport_number && (
                <span className="text-[10px] text-slate-400 ml-auto">{p.passport_number}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

function PassengersSection({
  leg,
  legIndex,
  currentOwnerPassengers,
  setLegs,
  setPassengerCount,
  updateLeg,
  updatePassenger,
  removePassenger,
  selectSuggestedPassenger,
  t,
  i18n,
}) {
  if (leg.is_ferry_flight) return null;
  // The "–" stepper may only drop EMPTY rows. When every row has data the user
  // must remove a specific passenger via its trash button, so the stepper is
  // disabled rather than silently erasing a filled row.
  const emptyRowCount = (leg.passengers || []).filter((p) => !paxHasContent(p)).length;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs text-slate-400 flex items-center gap-1">
          <UsersIcon className="w-3.5 h-3.5" /> {t("newTrip.passengersLabel")}
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPassengerCount(legIndex, (leg.passenger_count || 1) - 1)}
            disabled={
              (leg.passenger_count || 1) <= (leg.is_ferry_flight ? 0 : 1) || emptyRowCount === 0
            }
            title={emptyRowCount === 0 ? t("newTrip.lowerCountBlocked") : undefined}
            className="w-6 h-6 rounded bg-slate-700 text-white text-sm hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            -
          </button>
          <span className="text-sm text-white font-medium w-6 text-center">
            {leg.passenger_count || 1}
          </span>
          <button
            type="button"
            onClick={() => setPassengerCount(legIndex, (leg.passenger_count || 1) + 1)}
            disabled={(leg.passenger_count || 1) >= 20}
            className="w-6 h-6 rounded bg-slate-700 text-white text-sm hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            +
          </button>
        </div>
      </div>
      {leg.is_international && (leg.passenger_count || 1) > 1 && (
        <label className="flex items-center gap-2 text-xs text-slate-400 mb-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={leg.same_address_for_all || false}
            onChange={(e) => {
              const checked = e.target.checked;
              updateLeg(legIndex, "same_address_for_all", checked);
              if (checked) {
                const firstAddr = leg.passengers[0]?.address || "";
                setLegs((prev) => {
                  const updated = [...prev];
                  updated[legIndex].passengers = updated[legIndex].passengers.map((p, i) =>
                    i === 0 ? p : { ...p, address: firstAddr }
                  );
                  return updated;
                });
              }
            }}
            className="w-4 h-4 rounded border-slate-600 bg-[#0f172a] text-blue-500"
          />
          {t("newTrip.sameAddressAll")}
        </label>
      )}
      {(leg.passenger_count || 1) > 0 && (
        <div className="space-y-2">
          {leg.passengers.map((pax, paxIndex) => (
            // Three columns: index gutter · field stack · clear-button gutter.
            // The clear button lives in its OWN fixed-width gutter so every
            // field row inside the center column shares the exact same width —
            // the name row no longer gets squeezed (narrower than the passport
            // rows below) by the trash button sitting beside it.
            <div key={paxIndex} className="flex gap-2 items-start">
              <span className="w-6 h-9 flex items-center justify-center text-xs text-slate-400 flex-shrink-0">
                {paxIndex + 1}.
              </span>
              <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                {/* Name row: first / middle / last (autocomplete dropdown anchored here). */}
                <div className="relative">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={pax.first_name || ""}
                      onChange={(e) =>
                        updatePassenger(legIndex, paxIndex, "first_name", e.target.value)
                      }
                      placeholder={t("common.firstName")}
                      className="flex-1 min-w-0 bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
                    />
                    <input
                      type="text"
                      value={pax.middle_name || ""}
                      onChange={(e) =>
                        updatePassenger(legIndex, paxIndex, "middle_name", e.target.value)
                      }
                      placeholder={t("common.middleName")}
                      className="flex-1 min-w-0 bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
                    />
                    <input
                      type="text"
                      value={pax.last_name || ""}
                      onChange={(e) =>
                        updatePassenger(legIndex, paxIndex, "last_name", e.target.value)
                      }
                      placeholder={t("common.lastName")}
                      className="flex-1 min-w-0 bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
                    />
                  </div>
                  {/* Passenger search suggestions: trigger as soon as user types in any name field;
                      hide when the typed pair already matches an existing roster entry exactly.
                      Filter out passengers already picked in another row of THIS leg, so the same
                      person can't be added twice to the same flight. */}
                  {currentOwnerPassengers.length > 0 && (
                    <PassengerSuggestions
                      paxFirst={pax.first_name}
                      paxMiddle={pax.middle_name}
                      paxLast={pax.last_name}
                      legPassengers={leg.passengers}
                      paxIndex={paxIndex}
                      currentOwnerPassengers={currentOwnerPassengers}
                      onSelect={(p) => selectSuggestedPassenger(legIndex, paxIndex, p)}
                    />
                  )}
                </div>
                {/* "Pick a saved passenger" — only while the row is still empty, so it
                    guides the first capture without cluttering an already-filled row. */}
                {currentOwnerPassengers.length > 0 && !pax.first_name && !pax.last_name && (
                  <SavedPassengerPicker
                    legPassengers={leg.passengers}
                    paxIndex={paxIndex}
                    currentOwnerPassengers={currentOwnerPassengers}
                    onSelect={(p) => selectSuggestedPassenger(legIndex, paxIndex, p)}
                    t={t}
                  />
                )}
                {/* Passport # + expiration (international only).
                    DatePicker className overrides match the bare input next to it. */}
                {leg.is_international && (
                  <div className="flex gap-2 items-stretch">
                    <input
                      type="text"
                      value={pax.passport_number}
                      onChange={(e) =>
                        updatePassenger(legIndex, paxIndex, "passport_number", e.target.value)
                      }
                      placeholder={t("newTrip.passportNumber")}
                      className="flex-1 min-w-0 bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
                    />
                    <div className="flex-1 min-w-0">
                      <DatePicker
                        value={pax.passport_expiration}
                        onChange={(val) =>
                          updatePassenger(legIndex, paxIndex, "passport_expiration", val)
                        }
                        placeholder={t("newTrip.passportExpiration")}
                        className="rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>
                )}
                {/* Passport issuing country + date of birth (international only). */}
                {leg.is_international && (
                  <div className="flex gap-2 items-stretch">
                    <CountrySelect
                      value={pax.passport_country}
                      onChange={(e) =>
                        updatePassenger(legIndex, paxIndex, "passport_country", e.target.value)
                      }
                      className="flex-1 min-w-0 bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF]"
                      placeholder={t("newTrip.passportCountryPlaceholder")}
                      lang={i18n.language}
                    />
                    <div className="flex-1 min-w-0">
                      <DatePicker
                        value={pax.dob || ""}
                        onChange={(val) => updatePassenger(legIndex, paxIndex, "dob", val)}
                        placeholder={t("newTrip.dobPlaceholder")}
                        className="rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>
                )}
                {leg.is_international && (
                  <input
                    type="text"
                    value={pax.address || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (leg.same_address_for_all) {
                        setLegs((prev) => {
                          const updated = [...prev];
                          updated[legIndex].passengers = updated[legIndex].passengers.map((p) => ({
                            ...p,
                            address: val,
                          }));
                          return updated;
                        });
                      } else {
                        updatePassenger(legIndex, paxIndex, "address", val);
                      }
                    }}
                    disabled={leg.same_address_for_all && paxIndex > 0}
                    placeholder={t("newTrip.addressHint")}
                    className="w-full bg-[#0f172a] border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#0052FF] disabled:opacity-40 disabled:cursor-not-allowed"
                  />
                )}
              </div>
              {/* Clear-all button: empties every field of this passenger row in one click.
                  Its own gutter keeps the field column above from shrinking. */}
              {(() => {
                const hasContent = paxHasContent(pax);
                return (
                  <button
                    type="button"
                    onClick={() => removePassenger(legIndex, paxIndex)}
                    disabled={!hasContent}
                    title={t("newTrip.deletePassengerTitle")}
                    className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-800 text-slate-400 hover:bg-red-500/20 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                );
              })()}
            </div>
          ))}
        </div>
      )}
      {(leg.passenger_count || 1) === 0 && (
        <p className="text-xs text-slate-400 italic">{t("common.noPassengersLeg")}</p>
      )}
    </div>
  );
}

// Memo barrier so a keystroke in leg N's PassengersSection doesn't re-render
// every OTHER leg's PassengersSection (parent passes new `legs` array ref).
export default React.memo(PassengersSection);
